import { useEffect, useState } from 'react';
import { useSearchParams, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, MapPin, Calendar, Clock, CreditCard, Banknote, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [booking, setBooking] = useState(null);

  // Check if this is a "Pay on Arrival" booking (from navigation state)
  const isPayOnArrival = location.state?.pay_on_arrival;
  const bookingRef = location.state?.reference;
  const bookingId = location.state?.booking_id;

  useEffect(() => {
    if (isPayOnArrival && bookingId) {
      // Pay on arrival - fetch booking details
      fetchBookingDetails(bookingId);
    } else if (sessionId) {
      // Legacy payment flow
      pollPaymentStatus(sessionId);
    } else if (!isPayOnArrival) {
      setStatus('error');
    }
  }, [sessionId, isPayOnArrival, bookingId]);

  const fetchBookingDetails = async (id) => {
    try {
      const response = await axios.get(`${API}/bookings/${id}`, { withCredentials: true });
      setBooking(response.data);
      setStatus('success');
    } catch (error) {
      console.error('Error fetching booking:', error);
      // Even if fetch fails, show success since booking was created
      setStatus('success');
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentInfo(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <main data-testid="booking-success-page" className="bg-white min-h-screen pt-20 flex items-center justify-center">
      <div className="max-w-md mx-auto px-6 text-center">
        {status === 'loading' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <Loader2 className="w-12 h-12 text-[#C41E3A] mx-auto animate-spin" />
            <h1 className="text-2xl font-semibold text-[#1d1d1f]">
              Processing...
            </h1>
            <p className="text-sm text-[#86868b]">
              Confirming your booking.
            </p>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-6"
          >
            <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-semibold text-[#1d1d1f]">
              Booking Confirmed!
            </h1>
            <p className="text-[#86868b]">
              Thank you for choosing MA Africa Fix & Detail. We'll contact you shortly to confirm your appointment.
            </p>
            
            {/* Email Spam Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-700">
                <span className="font-medium">Confirmation email sent!</span> If you don't see it in your inbox, please check your spam or junk folder.
              </p>
            </div>
            
            {/* Reference Number */}
            {(bookingRef || booking?.reference_number) && (
              <div className="bg-[#1d1d1f] rounded-xl p-4 text-center">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Booking Reference</p>
                <p className="text-xl font-bold text-white font-mono tracking-wider">
                  {bookingRef || booking?.reference_number}
                </p>
              </div>
            )}

            {/* Pay on Arrival Notice */}
            {isPayOnArrival && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center justify-center gap-2 text-amber-700 mb-2">
                  <Banknote className="w-5 h-5" />
                  <span className="font-semibold">Pay on Arrival</span>
                </div>
                <p className="text-sm text-amber-600">
                  Please bring payment (Cash or Card) when you arrive at our workshop.
                </p>
                {booking?.total_price && (
                  <p className="text-lg font-bold text-amber-800 mt-2">
                    Amount Due: R{booking.total_price.toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {/* Booking Details */}
            {booking && (
              <div className="bg-[#f5f5f7] rounded-xl p-4 text-left space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-[#86868b]" />
                  <span className="text-sm text-[#1d1d1f]">{booking.preferred_date}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-[#86868b]" />
                  <span className="text-sm text-[#1d1d1f]">{booking.preferred_time}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-[#86868b]" />
                  <span className="text-sm text-[#1d1d1f]">
                    68 Serenade Road, Henville, Germiston
                  </span>
                </div>
              </div>
            )}
            
            {/* Legacy payment info display */}
            {paymentInfo && !isPayOnArrival && (
              <div className="bg-[#f5f5f7] rounded-xl p-4 text-left">
                <div className="flex justify-between text-sm">
                  <span className="text-[#86868b]">Amount paid</span>
                  <span className="text-[#1d1d1f]">R{(paymentInfo.amount_total / 100).toLocaleString()}</span>
                </div>
                {paymentInfo.booking_id && (
                  <div className="mt-2 pt-2 border-t border-[#d2d2d7]">
                    <Link
                      to={`/track/${paymentInfo.booking_id}`}
                      className="text-sm text-[#C41E3A] hover:underline"
                    >
                      Track your service →
                    </Link>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-3 pt-4">
              <Link
                to={`/track${bookingRef ? `?ref=${bookingRef}` : ''}`}
                className="w-full py-3 bg-[#C41E3A] text-white rounded-xl font-semibold hover:bg-[#a01830] transition-colors"
                data-testid="track-service"
              >
                Track My Service
              </Link>
              <Link
                to="/my-bookings"
                className="w-full py-3 bg-[#f5f5f7] text-[#1d1d1f] rounded-xl font-semibold hover:bg-[#e8e8ed] transition-colors"
                data-testid="my-bookings"
              >
                View My Bookings
              </Link>
              <Link
                to="/"
                className="w-full py-3 text-[#86868b] hover:text-[#1d1d1f] font-medium transition-colors"
                data-testid="back-home"
              >
                Back to Home
              </Link>
            </div>
          </motion.div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-6"
          >
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-semibold text-[#1d1d1f]">
              Something went wrong.
            </h1>
            <p className="text-sm text-[#86868b]">
              {status === 'expired' 
                ? 'Your session has expired. Please try again.'
                : 'We couldn\'t confirm your booking. Please contact us.'}
            </p>
            <div className="flex flex-col gap-3 pt-4">
              <Link
                to="/booking"
                className="w-full py-3 bg-[#C41E3A] text-white rounded-xl font-semibold hover:bg-[#a01830] transition-colors"
              >
                Try again
              </Link>
              <Link
                to="/contact-us"
                className="w-full py-3 bg-[#f5f5f7] text-[#1d1d1f] rounded-xl font-semibold hover:bg-[#e8e8ed] transition-colors"
              >
                Contact us
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
}
